import { Dayjs } from "dayjs";
import { AllReturnsList } from "./common";
import { DealerAccount } from "../../../../api/dealer/types";

export enum ListReturnsActionKind {
  SET_RETURNS = "SET_RETURNS",
  SET_TAB_INDEX = "SET_TAB_INDEX",
  SET_IS_LOADING = "SET_IS_LOADING",
  SET_LANGUAGE = "SET_LANGUAGE",
  SET_ERROR = "SET_ERROR",
  SET_IS_RETURNS_LOADED = "SET_IS_RETURNS_LOADED",
  SET_SHOW_CANCEL_MODAL = "SET_SHOW_CANCEL_MODAL",
  SET_RETURN_ATTRIBUTES_TO_CANCEL = "SET_RETURN_ATTRIBUTES_TO_CANCEL",
  CLEAR_FILTER = "CLEAR_FILTER",
  SET_FILTER = "SET_FILTER",
  SET_PAGE_ROWS_SIZE = "SET_PAGE_ROWS_SIZE",
  PREVIOUS_PAGE = "PREVIOUS_PAGE",
  NEXT_PAGE = "NEXT_PAGE",
  FIRST_PAGE = "FIRST_PAGE",
  LAST_PAGE = "LAST_PAGE",
  SET_IS_FILTER_APPLIED = "SET_IS_FILTER_APPLIED",
  SET_DATE_RANGE_FILTER_ACTION = "SET_DATE_RANGE_FILTER_ACTION",
  SET_SORT_PARAMS_ACTION = "SET_SORT_PARAMS_ACTION",
  UPDATE_SELECTED_ACCOUNTS = "UPDATE_SELECTED_ACCOUNTS",
  CLEAN_FILTERS = "CLEAN_FILTERS",
}

type SetReturnsAction = {
  type: ListReturnsActionKind.SET_RETURNS;
  payload: AllReturnsList;
};

type SetTabIndexAction = {
  type: ListReturnsActionKind.SET_TAB_INDEX;
  payload: number;
};

type SetIsLoadingAction = {
  type: ListReturnsActionKind.SET_IS_LOADING;
  payload: boolean;
};

type SetLanguageAction = {
  type: ListReturnsActionKind.SET_LANGUAGE;
  payload: string;
};

type SetErrorAction = {
  type: ListReturnsActionKind.SET_ERROR;
  payload: any;
};

type SetIsReturnsLoadedAction = {
  type: ListReturnsActionKind.SET_IS_RETURNS_LOADED;
  payload: boolean;
};

type SetShowCancelModalAction = {
  type: ListReturnsActionKind.SET_SHOW_CANCEL_MODAL;
  payload: boolean;
};

type SetReturnAttributesToCancelAction = {
  type: ListReturnsActionKind.SET_RETURN_ATTRIBUTES_TO_CANCEL;
  payload: {
    returnId: string;
    returnTypeCode: string;
    returnStatusList: string[];
  };
};

type SetSortParamsAction = {
  type: ListReturnsActionKind.SET_SORT_PARAMS_ACTION;
  payload: { field: string; sort: string };
};

type SetClearFilterAction = {
  type: ListReturnsActionKind.CLEAR_FILTER;
  payload: string;
};

type SetFilterAction = {
  type: ListReturnsActionKind.SET_FILTER;
  payload: { value: string; type: string };
};

type SetIsFilterAppliedAction = {
  type: ListReturnsActionKind.SET_IS_FILTER_APPLIED;
  payload: boolean;
};

type SetPageRowsSizeAction = {
  type: ListReturnsActionKind.SET_PAGE_ROWS_SIZE;
  payload: number;
};

type PreviousPageAction = {
  type: ListReturnsActionKind.PREVIOUS_PAGE;
};

type NextPageAction = {
  type: ListReturnsActionKind.NEXT_PAGE;
};

type FirstPageAction = {
  type: ListReturnsActionKind.FIRST_PAGE;
};

type LastPageAction = {
  type: ListReturnsActionKind.LAST_PAGE;
};

type SetDateRangeFilterAction = {
  type: ListReturnsActionKind.SET_DATE_RANGE_FILTER_ACTION;
  payload: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
};

type UpdateSelectedAccounts = {
  type: ListReturnsActionKind.UPDATE_SELECTED_ACCOUNTS;
  payload: {
    selectedAccounts: DealerAccount[];
    isSurplusReturnsEnabled: boolean;
  };
};

type CleanFiltersAction = {
  type: ListReturnsActionKind.CLEAN_FILTERS;
};

export type ListReturnsActions =
  | SetReturnsAction
  | SetTabIndexAction
  | SetIsLoadingAction
  | SetLanguageAction
  | SetErrorAction
  | SetIsReturnsLoadedAction
  | SetShowCancelModalAction
  | SetReturnAttributesToCancelAction
  | SetClearFilterAction
  | SetFilterAction
  | SetPageRowsSizeAction
  | PreviousPageAction
  | NextPageAction
  | FirstPageAction
  | LastPageAction
  | SetIsFilterAppliedAction
  | SetDateRangeFilterAction
  | SetSortParamsAction
  | UpdateSelectedAccounts
  | CleanFiltersAction;

export function setReturnsAction(
  returnsResponse: AllReturnsList
): SetReturnsAction {
  return {
    type: ListReturnsActionKind.SET_RETURNS,
    payload: returnsResponse,
  };
}

export function setTabIndexAction(index: number): SetTabIndexAction {
  return {
    type: ListReturnsActionKind.SET_TAB_INDEX,
    payload: index,
  };
}

export function setIsLoadingAction(isLoading: boolean): SetIsLoadingAction {
  return {
    type: ListReturnsActionKind.SET_IS_LOADING,
    payload: isLoading,
  };
}

export function setLanguageAction(language: string): SetLanguageAction {
  return {
    type: ListReturnsActionKind.SET_LANGUAGE,
    payload: language,
  };
}

export function setErrorAction(error: any): SetErrorAction {
  return {
    type: ListReturnsActionKind.SET_ERROR,
    payload: error,
  };
}

export function setIsReturnsLoaded(
  isReturnsLoaded: boolean
): SetIsReturnsLoadedAction {
  return {
    type: ListReturnsActionKind.SET_IS_RETURNS_LOADED,
    payload: isReturnsLoaded,
  };
}

export function setShowCancelModal(
  showCancelModal: boolean
): SetShowCancelModalAction {
  return {
    type: ListReturnsActionKind.SET_SHOW_CANCEL_MODAL,
    payload: showCancelModal,
  };
}

export function setReturnAttributesToCancelAction(returnAttributes: {
  returnId: string;
  returnTypeCode: string;
  returnStatusList: string[];
}): SetReturnAttributesToCancelAction {
  return {
    type: ListReturnsActionKind.SET_RETURN_ATTRIBUTES_TO_CANCEL,
    payload: returnAttributes,
  };
}

export function setSortParamsAction(
  field: string | any,
  sort: string | any
): SetSortParamsAction {
  return {
    type: ListReturnsActionKind.SET_SORT_PARAMS_ACTION,
    payload: { field, sort },
  };
}

export function setClearFilterAction(type: string): SetClearFilterAction {
  return {
    type: ListReturnsActionKind.CLEAR_FILTER,
    payload: type,
  };
}

export function setFilterAction(value: string, type: string): SetFilterAction {
  return {
    type: ListReturnsActionKind.SET_FILTER,
    payload: { value, type },
  };
}

export function setPageRowsSizeAction(
  pageRowSize: number
): SetPageRowsSizeAction {
  return {
    type: ListReturnsActionKind.SET_PAGE_ROWS_SIZE,
    payload: pageRowSize,
  };
}

export function previousPageAction(): PreviousPageAction {
  return {
    type: ListReturnsActionKind.PREVIOUS_PAGE,
  };
}

export function nextPageAction(): NextPageAction {
  return {
    type: ListReturnsActionKind.NEXT_PAGE,
  };
}

export function firstPageAction(): FirstPageAction {
  return {
    type: ListReturnsActionKind.FIRST_PAGE,
  };
}

export function lastPageAction(): LastPageAction {
  return {
    type: ListReturnsActionKind.LAST_PAGE,
  };
}

export function setIsFilterApplied(
  isFilterApplied: boolean
): SetIsFilterAppliedAction {
  return {
    type: ListReturnsActionKind.SET_IS_FILTER_APPLIED,
    payload: isFilterApplied,
  };
}

export function setDateRangeFilterAction(
  startDate: Dayjs,
  endDate: Dayjs
): SetDateRangeFilterAction {
  return {
    type: ListReturnsActionKind.SET_DATE_RANGE_FILTER_ACTION,
    payload: { startDate, endDate },
  };
}

export function updateSelectedAccountsAction(
  selectedAccounts: DealerAccount[],
  isSurplusReturnsEnabled: boolean
): UpdateSelectedAccounts {
  return {
    type: ListReturnsActionKind.UPDATE_SELECTED_ACCOUNTS,
    payload: { selectedAccounts, isSurplusReturnsEnabled },
  };
}

export function cleanFiltersAction(): CleanFiltersAction {
  return {
    type: ListReturnsActionKind.CLEAN_FILTERS,
  };
}
